
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const notificationVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[swiping=true]:transition-transform data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-top-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
        success: "border-green-600 bg-green-50 dark:bg-green-950/50 text-green-800 dark:text-green-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface SwipeableNotificationProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof notificationVariants> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
}

export const SwipeableNotification = React.forwardRef<
  HTMLDivElement,
  SwipeableNotificationProps
>(
  (
    {
      className, 
      variant,
      children, 
      open, 
      onOpenChange,
      duration = 3000,
      ...props
    }, 
    ref
  ) => {
    const touchStartRef = React.useRef<number | null>(null);
    const [isSwiping, setIsSwiping] = React.useState(false);
    const [swipeOffset, setSwipeOffset] = React.useState(0);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Setup auto-dismiss timer
    React.useEffect(() => {
      if (open && duration !== Infinity) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onOpenChange?.(false);
        }, duration);
      }
      
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [open, duration, onOpenChange]);

    // Haptic feedback on open
    React.useEffect(() => {
      if (open && "vibrate" in navigator) {
        try {
          navigator.vibrate(10); // Subtle vibration 
        } catch (e) {
          console.log("Haptic feedback not supported");
        }
      }
    }, [open]);

    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (touchStartRef.current === null) return;
      
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartRef.current - touchY;
      
      if (deltaY > 0) {
        setIsSwiping(true);
        setSwipeOffset(deltaY);
      }
    };

    const handleTouchEnd = () => {
      if (swipeOffset > 50) {
        // Swipe threshold reached
        onOpenChange?.(false);
        
        // Haptic feedback on dismiss
        if ("vibrate" in navigator) {
          try {
            navigator.vibrate(8);
          } catch (e) {
            console.log("Haptic feedback not supported");
          }
        }
      }
      
      touchStartRef.current = null;
      setIsSwiping(false);
      setSwipeOffset(0);
    };

    const style = {
      transform: isSwiping ? `translateY(-${swipeOffset}px)` : undefined,
    };

    return (
      <div
        ref={ref}
        className={cn(
          notificationVariants({ variant }),
          className
        )}
        data-state={open ? "open" : "closed"}
        data-swiping={isSwiping}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={style}
        role="status"
        aria-live="polite"
        {...props}
      >
        <div className="grid gap-1">
          {children}
        </div>
        <button
          className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
          onClick={() => onOpenChange?.(false)}
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);

SwipeableNotification.displayName = "SwipeableNotification";

export const SwipeableNotificationTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
));

SwipeableNotificationTitle.displayName = "SwipeableNotificationTitle";

export const SwipeableNotificationDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
));

SwipeableNotificationDescription.displayName = "SwipeableNotificationDescription";
